import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  Flex,
  Text,
  useTheme,
  Divider,
  Spinner,
  Center,
  Input,
} from '@chakra-ui/react';
import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import ChatComment, {ChatComment as ChatCommentInterface} from '../ChatComment';
import CommentInput from '../ChatComment/CommentInput';
import {TextG14} from '../../typography';
import useAuth from '../../../hooks/useAuth';

interface CommentContextModalProps {
  comment: ChatCommentInterface | null;
  onClose: () => void;
  onViewFullThread?: () => void;
  eventId?: number;
  communityId?: number;
  placeId?: number;
  addComment?: (content: string, parent_id?: string, imageURLs?: Array<any>) => Promise<any> | void;
  updateComment?: (
    commentId: string,
    content: string,
    parent_id?: string,
    imageURLs?: Array<any>
  ) => void;
  deleteComment?: (commentId: string) => void;
  reportComment?: (commentId: string) => void;
  helpfulComment?: (commentId: string) => void;
  fetchUsersHook?: any;
  fetchCommentsHook?: any;
}

const CommentContextModal = ({
  comment,
  onClose,
  onViewFullThread,
  eventId,
  communityId,
  placeId,
  addComment,
  updateComment,
  deleteComment,
  reportComment,
  helpfulComment,
  fetchUsersHook,
  fetchCommentsHook,
}: CommentContextModalProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {user} = useAuth();
  const [replyToComment, setReplyToComment] = useState<ChatCommentInterface | null>(null);
  const [inputOpen, setInputOpen] = useState(false);
  const [replies, setReplies] = useState<ChatCommentInterface[]>([]);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);

  // Fetch thread comments to find replies to this comment
  const articleId = eventId || communityId || placeId;
  const {data: commentsData, isLoading: commentsLoading} = fetchCommentsHook 
    ? fetchCommentsHook(articleId, !!comment)
    : {data: null, isLoading: false};

  // Extract replies to the current comment from the thread
  useEffect(() => {
    if (!comment || !commentsData?.pages) return;
    
    setIsLoadingReplies(true);
    const allComments: ChatCommentInterface[] = [];
    commentsData.pages.forEach((page: any) => {
      page.data.data.forEach((c: ChatCommentInterface) => {
        allComments.push(c);
      });
    });

    // Find all replies to this specific comment
    const commentReplies = allComments.filter(
      c => c.parent_id === comment.id
    ).sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    // Update local replies, replacing optimistic ones with real data
    setReplies(prev => {
      // Create a map of real replies by creation time for matching
      const realRepliesMap = new Map(commentReplies.map(r => [r.id, r]));
      
      // Keep optimistic replies that don't have a real counterpart yet
      // and replace them once we find a matching real reply
      const updatedReplies = [...commentReplies];
      
      // Add any optimistic replies that don't have a real version yet
      prev.forEach(optimisticReply => {
        if (optimisticReply.id.startsWith('temp-')) {
          // Check if we already have a real version by comparing content and timestamp proximity
          const hasRealVersion = commentReplies.some(realReply => 
            realReply.content === optimisticReply.content &&
            Math.abs(new Date(realReply.created_at).getTime() - new Date(optimisticReply.created_at).getTime()) < 5000
          );
          
          if (!hasRealVersion) {
            // This optimistic reply hasn't been replaced yet, keep it
            updatedReplies.push(optimisticReply);
          }
        }
      });
      
      // Sort again to maintain chronological order
      return updatedReplies.sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });
    
    setIsLoadingReplies(false);
  }, [commentsData, comment]);

  if (!comment) return null;

  const handleAddComment = async (content: string, parent_id?: string, imageURLs?: Array<any>) => {
    if (addComment) {
      // Store the temp ID for this optimistic reply
      const tempId = `temp-${Date.now()}`;
      
      // Optimistically add the reply to local state so user sees it immediately
      if (parent_id === comment.id) {
        const optimisticReply = {
          id: tempId,
          content,
          user_id: user?.id || '', 
          parent_id: comment.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user: {
            id: user?.id!,
            handle: user?.handle!,
            is_walkabout_employee: user?.isWalkaboutEmployee,
            profilePhotoUrl: user?.profilePhotoUrl,
          } as any,
          parent: comment,
          event_id: eventId,
          community_id: communityId,
          place_id: placeId,
          is_featured: false,
          is_abusive: false,
          helpful_count: 0,
          mentions: [],
        } as unknown as ChatCommentInterface;
        
        setReplies(prev => [...prev, optimisticReply]);
      }
      
      // Call addComment and wait for the result
      try {
        const result = await addComment(content, parent_id, imageURLs);
        
        // If we got a real comment back, replace the optimistic one
        if (result && result.data && parent_id === comment.id) {
          const realComment = result.data as ChatCommentInterface;
          setReplies(prev => 
            prev.map(reply => {
              if (reply.id === tempId) {
                // Preserve the user data from the optimistic comment since we know it's the current user
                return {
                  ...realComment, 
                  parent: comment,
                  user: {
                    id: user?.id!,
                    handle: user?.handle!,
                    is_walkabout_employee: user?.isWalkaboutEmployee,
                    profilePhotoUrl: user?.profilePhotoUrl,
                  } as any,
                };
              }
              return reply;
            })
          );
        }
      } catch (error) {
        // If the API call fails, remove the optimistic reply
        if (parent_id === comment.id) {
          setReplies(prev => prev.filter(reply => reply.id !== tempId));
        }
        console.error('Failed to add comment:', error);
      }
      
      setInputOpen(false);
      setReplyToComment(null);
    }
  };

  const handleReplyClick = (commentToReply: ChatCommentInterface) => {
    setReplyToComment(commentToReply);
    setInputOpen(true);
  };

  const handleScrollToParent = (parentId: string) => {
    // For nested replies in modal, we need to close current modal and reopen with parent
    // This provides consistent modal-based navigation for nested replies
    const parentReply = replies.find((r) => r.id === parentId);
    
    if (parentReply) {
      // Close current modal and trigger opening parent's thread
      onClose();
    } else if (comment.parent_id === parentId) {
      // They clicked on the original parent that's already showing
      // Could scroll to it or just ignore
    }
  };

  const handleUpdateComment = (
    commentId: string,
    content: string,
    parent_id?: string,
    imageURLs?: Array<any>
  ) => {
    // Update the local replies state immediately
    setReplies(prev => 
      prev.map(reply => 
        reply.id === commentId 
          ? {...reply, content, updated_at: new Date().toISOString()} 
          : reply
      )
    );
    
    // Call the parent's updateComment if it exists
    if (updateComment) {
      updateComment(commentId, content, parent_id, imageURLs);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    // Mark the comment as deleted in local replies state immediately
    setReplies(prev => 
      prev.map(reply => 
        reply.id === commentId 
          ? {...reply, content: 'Message deleted by author', status: 0} 
          : reply
      )
    );
    
    // Call the parent's deleteComment if it exists
    if (deleteComment) {
      deleteComment(commentId);
    }
  };

  const handleViewFullThread = () => {
    onClose();
    
    // Navigate to Thread tab
    if (eventId) {
      navigate(`/event/${eventId}/chat?tab=Thread`);
    } else if (communityId) {
      navigate(`/community/${communityId}/chat?tab=Thread`);
    } else if (placeId) {
      navigate(`/place/${placeId}/chat?tab=Thread`);
    }
    
    if (onViewFullThread) {
      onViewFullThread();
    }
  };

  return (
    <Modal isOpen={!!comment} onClose={onClose} size="full" scrollBehavior="outside">
      <ModalOverlay />
      <ModalContent h="100vh" display="flex" flexDirection="column">
        <ModalHeader>
          <Text fontSize="lg" fontWeight="600">
            Conversation
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={0} flex={1} overflowY="auto">
          <Flex flexDir="column" gap={4} pb={inputOpen ? 4 : 20}>
            {/* Show parent comment if this is a reply */}
            {comment.parent && (
              <>
                <Box>
                  <TextG14 color={theme.colors.grey} mb={2}>
                    Original message:
                  </TextG14>
                  <ChatComment
                    fetchUsersHook={fetchUsersHook || (() => undefined)}
                    comment={comment.parent}
                    articleId={eventId || communityId || placeId}
                    name=""
                    isRSVPd={true}
                    addComment={addComment || (() => {})}
                    updateComment={handleUpdateComment}
                    deleteComment={handleDeleteComment}
                    reportComment={reportComment || (() => {})}
                    helpfulComment={helpfulComment || (() => {})}
                    setInputOpen={setInputOpen}
                    setInputHidden={() => {}}
                    showUserRSVP={false}
                    isGroupStart={true}
                    isGroupEnd={true}
                    onReplyClick={handleReplyClick}
                    onScrollToParent={handleScrollToParent}
                  />
                </Box>
                <Divider />
              </>
            )}

            {/* Show the focused comment */}
            <Box>
              {comment.parent && (
                <TextG14 color={theme.colors.grey} mb={2}>
                  Their reply:
                </TextG14>
              )}
              <Box
                bg={theme.colors.lightestBlue || '#F0F8FF'}
                borderRadius="10px"
                p={3}
                border={`2px solid ${theme.colors.lightBlue}`}>
                <ChatComment
                  fetchUsersHook={fetchUsersHook || (() => undefined)}
                  comment={comment}
                  articleId={eventId || communityId || placeId}
                  name=""
                  isRSVPd={true}
                  addComment={addComment || (() => {})}
                  updateComment={handleUpdateComment}
                  deleteComment={handleDeleteComment}
                  reportComment={reportComment || (() => {})}
                  helpfulComment={helpfulComment || (() => {})}
                  setInputOpen={setInputOpen}
                  setInputHidden={() => {}}
                  showUserRSVP={false}
                  isGroupStart={true}
                  isGroupEnd={true}
                  onReplyClick={handleReplyClick}
                />
              </Box>
            </Box>

            {/* Show replies to this comment */}
            {isLoadingReplies && (
              <Center py={4}>
                <Spinner size="sm" color={theme.colors.lightBlue} />
              </Center>
            )}
            
            {!isLoadingReplies && replies.length > 0 && (
              <>
                <Divider />
                <Box>
                  {/* <TextG14 color={theme.colors.grey} mb={2}>
                    {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}:
                  </TextG14> */}
                  <Flex flexDir="column" gap={3}>
                    {replies.map((reply) => (
                      <ChatComment
                        key={reply.id}
                        fetchUsersHook={fetchUsersHook || (() => undefined)}
                        comment={reply}
                        articleId={eventId || communityId || placeId}
                        name=""
                        isRSVPd={true}
                        addComment={addComment || (() => {})}
                        updateComment={handleUpdateComment}
                        deleteComment={handleDeleteComment}
                        reportComment={reportComment || (() => {})}
                        helpfulComment={helpfulComment || (() => {})}
                        setInputOpen={setInputOpen}
                        setInputHidden={() => {}}
                        showUserRSVP={false}
                        isGroupStart={true}
                        isGroupEnd={true}
                        onReplyClick={handleReplyClick}
                      />
                    ))}
                  </Flex>
                </Box>
              </>
            )}

            {/* View Full Thread button */}
            <Button
              colorScheme="blue"
              onClick={handleViewFullThread}
              mt={4}
              mb={4}
              size="lg"
              width="100%">
              View Full Thread
            </Button>
          </Flex>
        </ModalBody>

        {/* Fixed Bottom Input Section */}
        <Box
          borderTop={`1px solid ${theme.colors.lightGrey}`}
          background="white"
          w="100%"
          px={4}
          pt={3}
          pb={3}>
          {!inputOpen ? (
            <Flex gap={2} align="center">
              <Input
                placeholder="Reply to this conversation..."
                onClick={() => setInputOpen(true)}
                readOnly
                flex={1}
                borderRadius="20px"
                border="1px solid rgb(226, 232, 240)"
                _focus={{
                  borderColor: theme.colors.blue,
                  boxShadow: 'none',
                }}
                _hover={{
                  borderColor: 'rgb(203, 213, 224)',
                }}
                px={4}
                py={2}
                fontSize="14px"
                cursor="pointer"
              />
            </Flex>
          ) : (
            <Box>
              <CommentInput
                fetchUsersHook={fetchUsersHook}
                articleId={eventId || communityId || placeId}
                onCloseClick={() => {
                  setInputOpen(false);
                  setReplyToComment(null);
                }}
                onSaveClick={handleAddComment}
                parentComment={replyToComment || comment}
              />
            </Box>
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default CommentContextModal;
